import { TextButton } from 'components/v2/buttons';
import FilesList from 'components/v2/file-row/FilesList';
import { ButtonUploadIcon } from 'components/v2/icons';
import { ImagesUpload } from 'components/v2/images-upload';
import SmartLabel from 'components/v2/labels/SmartLabel';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EPDModel, FileLoadingModel, MaterialPropertyModel, VersionStateType } from 'services/EpdClient';
import EPDService from 'services/EpdService';
import MaterialPropertiesService from 'services/MaterialPropertiesService';
import styled from 'styled-components';
import { ErrorTextBox, ReadMoreInlineButton } from 'styles/v2/Styles.styled';
import { DictionaryValue, Option } from 'types/types';
import UploadDocumentsDialog from '../dialogs/UploadDocumentsDialog';
import EpdDefinition from './definition/EpdDefinition';
import MachineReadable from './machine-readable/MachineReadable';
import ProductInformation from './product-information/ProductInformation';
import EpdService from 'services/EpdService';
import { STATUS_COMMENT_MAX_LENGTH } from 'constants/constants';
import StatusCommentFullViewDialog from 'components/v2/dialogs/StatusCommentFullViewDialog';

type TProps = {
  epd: EPDModel;
  onChangeEpd(name: string, value: any): Promise<void>;
  onUploadFile(field: string, file: File, documentTypeId: string | null, documentLanguageId: string | null): void;
  onRemoveFile(fileId: string): Promise<void>;
  isConstruction?: boolean;
  title: string | undefined;
  setTitle: any;
  isReadOnly: boolean;
  canUpload: boolean;
  fileLoading?: FileLoadingModel[];
  refreshEPD(epdId: string): Promise<void>;
};

const EpdDetails = ({
  epd,
  onChangeEpd,
  onUploadFile,
  onRemoveFile,
  isConstruction,
  title,
  setTitle,
  isReadOnly,
  canUpload,
  fileLoading,
  refreshEPD,
}: TProps) => {
  const { t } = useTranslation();
  const [isUploadOpened, setIsUploadOpened] = useState<boolean>(false);
  const [showVerificationDeclineReasonFullCommentDialog, setShowVerificationDeclineReasonFullCommentDialog] =
    useState(false);

  const [materialProperties, setMaterialProperties] = useState<MaterialPropertyModel[]>([]);
  const [biogenicCarbonContentUnits, setBiogenicCarbonContentUnits] = useState<Option[]>([]);

  const verificationDeclineReasonPostProcessed =
    epd.verificationDeclineReason &&
    (epd.verificationDeclineReason.length > STATUS_COMMENT_MAX_LENGTH
      ? epd.verificationDeclineReason.slice(0, STATUS_COMMENT_MAX_LENGTH) + '…'
      : epd.verificationDeclineReason);

  useEffect(() => {
    const fetchMaterialProperties = async () => {
      const res = await MaterialPropertiesService.getMaterialProperties();
      setMaterialProperties(res);
    };

    const fetchBiogenicCarbonContentUnits = async () => {
      const result = await EpdService.getBiogenicCarbonContentUnits();
      setBiogenicCarbonContentUnits(
        result.map(
          (x: DictionaryValue) =>
            ({
              value: x.key.toString(),
              label: x.value,
            } as Option)
        )
      );
    };

    fetchMaterialProperties();
    fetchBiogenicCarbonContentUnits();
  }, []);

  return (
    <>
      {epd.versionState === VersionStateType.DeclinedVerification && (
        <DeclinedReason>
          <ErrorTextBox>
            {verificationDeclineReasonPostProcessed || 'EPD has been declined on verification step.'}{' '}
            {verificationDeclineReasonPostProcessed &&
              verificationDeclineReasonPostProcessed.length > STATUS_COMMENT_MAX_LENGTH && (
                <ReadMoreInlineButton onClick={() => setShowVerificationDeclineReasonFullCommentDialog(true)}>
                  Read more
                </ReadMoreInlineButton>
              )}
          </ErrorTextBox>
        </DeclinedReason>
      )}

      <EpdDefinition epd={epd} onChangeEpd={onChangeEpd} isReadOnly={isReadOnly} title={title} setTitle={setTitle} />

      <ProductInformation
        epd={epd}
        onChangeEpd={onChangeEpd}
        onUploadFile={(field, file) => onUploadFile(field, file, null, null)}
        onRemoveFile={onRemoveFile}
        isReadOnly={isReadOnly}
      />

      <SmartLabel label={'Product images'} required hasValue={epd?.productImages && epd?.productImages.length > 0} />
      <ImagesUpload
        name={t('epdWizard.wizardStep2.properties.images')}
        images={epd?.productImages}
        onUpload={(e: any) => {}}
        onReorder={(imgIds: string[]) => EPDService.arrangeEpdFiles(imgIds)}
        onRemove={() => {}}
        disabled
      />

      <div className="my-3">
        <SmartLabel label={'EPD documents'} required hasValue={epd?.documents && epd?.documents.length > 0} />
        <FilesList files={epd?.documents} disabled />

        {canUpload && (
          <>
            <TextButton
              className="white-space-nowrap pt-0"
              label="Upload documents"
              icon={ButtonUploadIcon}
              text
              onClick={() => setIsUploadOpened(true)}
            />
            <UploadDocumentsDialog
              header="Upload documents"
              isOpened={isUploadOpened}
              onHide={() => setIsUploadOpened(false)}
              epd={epd}
              onChangeEpd={onChangeEpd}
              onUploadFile={onUploadFile}
              onRemoveFile={onRemoveFile}
              isConstruction={isConstruction}
              fileLoading={fileLoading}
              refreshEPD={refreshEPD}
            />
          </>
        )}
      </div>

      <MachineReadable
        epd={epd}
        onChangeEpd={onChangeEpd}
        isConstruction={isConstruction}
        isReadOnly={isReadOnly}
        materialProperties={materialProperties}
        biogenicCarbonContentUnits={biogenicCarbonContentUnits}
      />

      <SmartLabel label="LCA results" required={isConstruction} hasValue={epd?.lcAs && epd?.lcAs.length > 0} />
      <FilesList files={epd?.lcAs} disabled />
      {canUpload && (
        <TextButton
          className="white-space-nowrap pt-0"
          label="Upload documents"
          icon={ButtonUploadIcon}
          text
          onClick={() => setIsUploadOpened(true)}
        />
      )}
      <StatusCommentFullViewDialog
        headerTitle={t('epdWizard.epdState.action.commentByVerifierTitle')}
        comment={epd.verificationDeclineReason!}
        isOpened={showVerificationDeclineReasonFullCommentDialog}
        onClose={() => setShowVerificationDeclineReasonFullCommentDialog(false)}
      />
    </>
  );
};

const DeclinedReason = styled.div`
  ${ErrorTextBox} {
    word-break: break-word;
  }
`;

export default EpdDetails;
